import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiUsers, FiDollarSign, FiShoppingBag } from 'react-icons/fi';
import { isAuthenticated } from '../../lib/auth';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import styles from './Dashboard.module.css';

interface Affiliate {
  id: string;
  email: string;
  name: string;
  cryptoAddress: string | null;
  cryptoNetwork: string | null;
  totalEarned: number;
  availableBalance: number;
  active: boolean;
  createdAt: string;
  _count: {
    promoCodes: number;
    commissions: number;
  };
}

const Affiliates: React.FC = () => {
  const navigate = useNavigate();
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
  }, [navigate]);

  const fetchAffiliates = React.useCallback(async () => {
    try {
      const res = await api.get('/admin/affiliates', {
        params: {
          search: searchTerm || undefined,
          page: currentPage,
          limit: 20,
        },
      });
      setAffiliates(res.data.affiliates);
      setTotalPages(res.data.pagination.totalPages);
    } catch (error) {
      console.error('Failed to fetch affiliates:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, currentPage]);

  useEffect(() => {
    fetchAffiliates();
  }, [fetchAffiliates]);

  const handleToggleStatus = async (id: string, active: boolean) => {
    try {
      await api.put(`/admin/affiliates/${id}/status`, { active: !active });
      fetchAffiliates();
    } catch (error) {
      console.error('Failed to toggle affiliate status:', error);
    }
  };

  const totalEarnedSum = affiliates.reduce((sum, a) => sum + a.totalEarned, 0);
  const totalBalanceSum = affiliates.reduce((sum, a) => sum + a.availableBalance, 0);
  const activeCount = affiliates.filter(a => a.active).length;

  return (
    <AdminLayout>
      <div className={styles.contentHeader}>
        <div>
          <h1>Affiliates Management</h1>
          <p>Manage affiliate partners and their earnings</p>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#667eea' }}>
            <FiUsers />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{affiliates.length}</div>
            <div className={styles.statLabel}>Total Affiliates</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#10b981' }}>
            <FiUsers />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{activeCount}</div>
            <div className={styles.statLabel}>Active Affiliates</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#f59e0b' }}>
            <FiDollarSign />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>${totalEarnedSum.toFixed(2)}</div>
            <div className={styles.statLabel}>Total Commissions Paid</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#ef4444' }}>
            <FiDollarSign />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>${totalBalanceSum.toFixed(2)}</div>
            <div className={styles.statLabel}>Pending Balance</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <input
            type="text"
            placeholder="Search affiliates..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <Link to="/admin/promo-codes" className={styles.filterBtn}>
          <FiShoppingBag /> Promo Codes
        </Link>
        <Link to="/admin/withdraws" className={styles.filterBtn}>
          <FiDollarSign /> Withdrawals
        </Link>
      </div>

      {/* Table */}
      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}>Loading...</div>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Codes</th>
                  <th>Sales</th>
                  <th>Total Earned</th>
                  <th>Balance</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {affiliates.map((affiliate) => (
                  <tr key={affiliate.id}>
                    <td>
                      <Link to={`/admin/affiliates/${affiliate.id}`} className={styles.link}>
                        {affiliate.name}
                      </Link>
                    </td>
                    <td>{affiliate.email}</td>
                    <td>{affiliate._count.promoCodes}</td>
                    <td>{affiliate._count.commissions}</td>
                    <td className={styles.price}>${affiliate.totalEarned.toFixed(2)}</td>
                    <td className={styles.price}>${affiliate.availableBalance.toFixed(2)}</td>
                    <td>
                      <span className={affiliate.active ? styles.statusPaid : styles.statusCancelled}>
                        {affiliate.active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td>{new Date(affiliate.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className={styles.actionBtn}
                        onClick={() => handleToggleStatus(affiliate.id, affiliate.active)}
                      >
                        {affiliate.active ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {affiliates.length === 0 && (
              <div className={styles.emptyState}>
                <p>No affiliates found</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </AdminLayout>
  );
};

export default Affiliates;
